// Ответы на уточнения — парами «вопрос — ответ». SMY-487 · 08.10.2026
//
// До этой правки ответы приклеивались к тексту запроса через «. », без самих
// вопросов: модель видела «Только официальные. Сегодня» и гадала, к чему это.
// Склейка жила в четырёх местах — отправка и кнопка под карточками, в кабинете
// и в приложении, — и правило поменялось бы в одном из них.
//
// Теперь уточнения едут отдельным полем запуска, а текст для модели собирается
// здесь, одним правилом на сервер и оба клиента:
//
//   {запрос}
//
//   Уточнения:
//   — {вопрос} — {ответ}
//
// ОДНА СТРОКА ВЕЗДЕ (решение владельца 08.10.2026). Тот же текст ложится в
// ленту (`messages.body`), в пузырь клиента и в историю следующего хода.
// Клиент сверяет пузырь с лентой точным равенством (`unseenBubbles` в
// api-client), поэтому сервер и клиент обязаны собирать его одной функцией с
// одной нормализацией — она здесь же, внутри `requestText`. Отвергнутая
// альтернатива — вопрос в ленте отдельно, пары новым столбцом `messages`: та
// же польза ценой миграции и правки ленты на обеих платформах.
//
// ПОЧЕМУ ВОПРОС РЕЖЕТСЯ, А ОТВЕТ НЕТ. Вопрос пишет модель-классификатор, а не
// человек: отказать человеку за длину чужого текста — тупик, из которого ему не
// выйти. Ответ пишет человек, и его ручка отклоняет за пределом словами
// (`routes/clarifications.ts`): молча обрезанный ответ — это задача, которую
// сделали не про то.
//
// Перевод строки внутри пары становится пробелом: иначе ответ «да\n— Сумма —
// 0» дописал бы модели пару, которой человеку не задавали. Правило одно с
// именем и консолью — `withoutControls`: своё `\s+` здесь не ловило NEL
// (U+0085), и он доезжал до модели разрывом строки (ворота ревью 1, 08.10.2026,
// dedup-hunter).

import {
  ADDED_SEND_CLOSED, REQUEST_SEND_CLOSED, requestLengthNote, requestTooLong,
} from './request-length.ts'
import { truncate, withoutControls } from './text.ts'

/** Вопрос уточнения и ответ человека на него — кнопкой или словами. */
export interface ClarificationPair {
  question: string
  answer: string
}

/** Потолок пар — тот же, что у вопросов классификатора: экран 03 — уточнение, а не анкета. */
export const CLARIFY_MAX_PAIRS = 3
export const CLARIFY_QUESTION_MAX_CHARS = 300
export const CLARIFY_ANSWER_MAX_CHARS = 500
export const CLARIFY_HEADING = 'Уточнения:'

/** Запрос и ответы на уточнения парами — то, что уходит запуском, на сервере и в обоих клиентах. */
export interface ClarifiedRequest {
  text: string
  clarifications?: ClarificationPair[]
}

const oneLine = (s: string): string => withoutControls(s).replace(/\s+/g, ' ').trim()

/**
 * Пары в том виде, в каком их увидит модель: в одну строку, вопрос — не длиннее
 * предела, без пар с пустым ответом (вопрос пропущен — пары нет). Число пар и
 * длину ответа здесь не режет никто: за пределом ручка отвечает отказом.
 */
export function normalizePairs(pairs: readonly ClarificationPair[]): ClarificationPair[] {
  return pairs
    .map((p) => ({ question: truncate(oneLine(p.question), CLARIFY_QUESTION_MAX_CHARS), answer: oneLine(p.answer) }))
    .filter((p) => p.answer !== '')
}

/**
 * Строки пар «— вопрос — ответ». Пары — уже из `normalizePairs`. Одна форма на
 * ленту и на классификатор: своя копия в `classify.ts` разошлась бы с тем, что
 * видят модель запуска и пузырь, при первой правке формы.
 */
export function pairLines(pairs: readonly ClarificationPair[]): string {
  return pairs.map((p) => `— ${p.question} — ${p.answer}`).join('\n')
}

/**
 * Текст запроса для модели, ленты и пузыря. Без пар — сам запрос, байт в байт:
 * так старый клиент, не знающий поля, получает прежнее поведение.
 */
export function requestText(text: string, pairs: readonly ClarificationPair[] = []): string {
  const shown = normalizePairs(pairs)
  if (shown.length === 0) return text
  return `${text}\n\n${CLARIFY_HEADING}\n${pairLines(shown)}`
}

/**
 * Запрос из тела ленты без блока «Уточнения» — для векторов тем (SMY-487,
 * ворота ревью 1, 08.10.2026, reviewer). Вопросы классификатора общие у многих
 * запросов («Какой бюджет?»), и с ними уточнённые запросы сближались по форме,
 * а не по смыслу.
 *
 * Срезается только хвост, который целиком похож на блок: заголовок и за ним
 * одни строки пар. Человек вправе набрать «Уточнения:» сам, и такой текст
 * остаётся как есть. Подделать блок своим текстом можно — цена этого только
 * вектор его же запроса; запрет и маскирование по-прежнему смотрят тело целиком.
 */
export function withoutClarifications(body: string): string {
  const mark = `\n\n${CLARIFY_HEADING}\n`
  const at = body.lastIndexOf(mark)
  if (at < 0) return body
  const tail = body.slice(at + mark.length).split('\n')
  return tail.every((l) => l.startsWith('— ')) ? body.slice(0, at) : body
}

/**
 * Запрос с ответами, но без вопросов, — для поисковика и картинки. Поисковик
 * ищет словами, и вопрос «Какой бюджет?» в запросе только мешает; картинке
 * вопрос не описывает ничего из того, что рисуют, а запрос её перерисовки
 * держится в `IMAGE_PROMPT_MAX_CHARS` — тратить его на вопросы незачем.
 */
export function requestInline(text: string, pairs: readonly ClarificationPair[] = []): string {
  return [text.trim(), ...normalizePairs(pairs).map((p) => p.answer)].reduce((acc, part) =>
    acc === '' ? part : /[.!?…]$/.test(acc) ? `${acc} ${part}` : `${acc}. ${part}`,
  )
}

/**
 * Карточка без готовых ответов — та, что просит «Ответь словами в поле ниже».
 * Одно правило на ядро и карточку экрана: разойдись они, ответ встал бы на
 * карточку, которую экран рисует кнопками, и его не было бы видно (ворота
 * ревью 3, dedup-hunter).
 */
export const asksWords = (q: { options?: readonly string[] }): boolean => (q.options?.length ?? 0) === 0

/** Карточки без готовых ответов, на которые ещё не ответили, — по порядку. Ответы — уже в одну строку. */
const waitingWords = (questions: readonly { options?: readonly string[] }[], answers: readonly string[]): number[] =>
  questions.flatMap((q, i) => (asksWords(q) && (answers[i] ?? '') === '' ? [i] : []))

/**
 * Что уходит с отправкой, пока на экране карточки уточнений. Решение владельца
 * 08.10.2026 (A1): набранное в поле — ответ, и отправка идёт как кнопкой под
 * карточками, без нового распознавания.
 *
 * На КАКОЙ вопрос — решение владельца того же дня по находке ворот ревью 1
 * (ux-states-checker): на первый без ответа и без готовых ответов. Такая карточка сама говорит
 * человеку «Ответь словами в поле ниже», и поле отвечает именно ей; раньше
 * адрес уходил ответом на пропущенный «Какой бюджет?» с кнопками, а вопрос про
 * адрес оставался пустым. Карточек без кнопок не осталось — первый без ответа.
 *
 * Отвечено всё, а в поле что-то набрано — дополнение к самой задаче, а не
 * ответ: оно встаёт в запрос, а не переписывает выбранное кнопкой. Тоже решение
 * владельца 08.10.2026; цена записана им же: на «нет, маслом» после кнопки
 * «Акварель» модель видит оба. Отвергнуты «заменить последний ответ» (теряется
 * кнопка, когда набранное — дополнение) и «отдельной парой» (подпись не прошла).
 */
export function clarifiedRequest(args: {
  question: string
  questions: readonly { question: string; options?: readonly string[] }[]
  answers: Readonly<Record<number, string | undefined>>
  typed?: string | undefined
}): Required<ClarifiedRequest> {
  const { answers, addition } = placeTyped(args)
  const text = addition != null ? requestInline(args.question, [{ question: '', answer: addition }]) : args.question
  return {
    text,
    clarifications: args.questions.flatMap((q, i) => {
      const answer = answers[i] ?? ''
      return answer !== '' ? [{ question: q.question, answer }] : []
    }),
  }
}

/**
 * Куда встаёт набранное при карточках: ответом на первую карточку без ответа
 * или, когда отвечено всё, добавкой к вопросу — уже в одну строку, в том виде,
 * что приклеится. Одно место на запрос (`clarifiedRequest`) и счёт длины у поля
 * (`fieldRequest`): разойдись они, поле велело бы сократить добавку не на то
 * число (ворота SMY-619, dedup-hunter).
 */
function placeTyped(args: {
  questions: readonly { question: string; options?: readonly string[] }[]
  answers: Readonly<Record<number, string | undefined>>
  typed?: string | undefined
}): { answers: string[]; addition: string | null } {
  const typed = oneLine(args.typed ?? '')
  const answers = args.questions.map((_, i) => oneLine(args.answers[i] ?? ''))
  const [words] = waitingWords(args.questions, answers)
  const free = words ?? answers.findIndex((a) => a === '')
  if (typed !== '' && free >= 0) answers[free] = typed
  return { answers, addition: typed !== '' && free < 0 ? typed : null }
}

/**
 * Отправка при карточках: запуск или ответ, который ждёт на карточке. Решение
 * владельца 14.10.2026 (ворота ревью 2, ux-states-checker): карточек без кнопок
 * бывает две-три — имя, дата, кому, — и первое же набранное запускало работу,
 * а на вторую ответить было нечем. Теперь ответ на карточку без кнопок, после
 * которого такие карточки ещё остаются без ответа, встаёт на неё и ждёт
 * (`hold`); запуск — кнопкой под карточками или ответом на последнюю из них.
 * Карточкам с кнопками поле по-прежнему отвечает и запускает сразу: их выбор —
 * кнопка, а не слова.
 *
 * Ответ длиннее `CLARIFY_ANSWER_MAX_CHARS` не ждёт: на карточке его поправить
 * нечем, а ручка его отклонит — тупик, выйти из которого можно, только сняв
 * все ответы (ux-writer, 14.10.2026). Он уходит сразу, и отказ возвращает его в
 * поле — сокращать там, где его набрали.
 */
export function clarifiedStep(args: {
  question: string
  questions: readonly { question: string; options?: readonly string[] }[]
  answers: Readonly<Record<number, string | undefined>>
  typed?: string | undefined
  /** Кнопка под карточками: запускает всегда, набранное не ждёт на карточке. */
  launch?: boolean
}):
  | { next: 'hold'; index: number; answer: string }
  | { next: 'go'; request: Required<ClarifiedRequest> } {
  const typed = oneLine(args.typed ?? '')
  const waiting = waitingWords(args.questions, args.questions.map((_, i) => oneLine(args.answers[i] ?? '')))
  const [first] = waiting
  if (args.launch !== true && typed !== '' && typed.length <= CLARIFY_ANSWER_MAX_CHARS && first != null && waiting.length > 1) {
    return { next: 'hold', index: first, answer: typed }
  }
  return { next: 'go', request: clarifiedRequest(args) }
}

/**
 * Текст из `fieldRequest` без длины добавки. Экраны её не зовут — счёт у поля
 * идёт через `fieldLength`; осталась для теста склейки.
 */
export function fieldRequestText(cards: FieldCards | null, typed: string): string {
  return fieldRequest(cards, typed).text
}

/** Карточки на экране — для счёта длины у поля. */
type FieldCards = {
  question: string
  questions: readonly { question: string; options?: readonly string[] }[]
  answers: Readonly<Record<number, string | undefined>>
}

/**
 * Текст запроса, который уйдёт с набранным в поле, и длина добавки к вопросу,
 * если набранное — добавка (`placeTyped`), иначе `null`. SMY-619 · 27.10.2026
 *
 * Без карточек это само набранное. При карточках набранное — ответ на карточку
 * (его предел свой, `CLARIFY_ANSWER_MAX_CHARS`) или, когда отвечено всё,
 * дополнение, приклеенное к вопросу (`clarifiedRequest`): тогда сервер считает
 * склеенный текст, и поле, считая одно набранное, пустило бы отправку, которой
 * ручка ответит 400 (ворота SMY-619, ux-states-checker).
 */
export function fieldRequest(cards: FieldCards | null, typed: string): { text: string; addition: number | null } {
  const own = { text: typed.trim(), addition: null }
  if (cards == null || own.text === '') return own
  const step = clarifiedStep({ ...cards, typed })
  if (step.next === 'hold') return own
  const { addition } = placeTyped({ ...cards, typed })
  return addition != null ? { text: step.request.text, addition: addition.length } : own
}

/**
 * Длина того, что уйдёт из поля: строка у поля, закрыта ли отправка и что
 * объявить, когда закрылась. SMY-619 · 27.10.2026
 *
 * Одна функция на оба экрана: раньше каждый собирал это из трёх вызовов, и
 * различать «набранное — весь запрос» и «набранное — добавка к вопросу
 * карточек» пришлось бы в четырёх местах. Добавка считается вместе с
 * вопросом, но строка говорит о ней самой (`requestLengthNote` с длиной добавки): вопрос ушёл
 * раньше, и сократить его в поле нельзя (e2e SMY-619).
 */
export function fieldLength(cards: FieldCards | null, typed: string): { note: string | null; tooLong: boolean; closed: string } {
  const { text, addition } = fieldRequest(cards, typed)
  return {
    note: requestLengthNote(text.length, addition),
    tooLong: requestTooLong(text),
    closed: addition != null ? ADDED_SEND_CLOSED : REQUEST_SEND_CLOSED,
  }
}
